import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import TourEditForm, {TourFormValues} from "./TourEditForm";
import {createTour} from "../../../api/tourApi";
import {localInputToUtc} from "../../../utils/formatDateTime";
import {MessageType} from "../../components/MessageBox";
import {UserRole} from "../../../models/account";
import {createTourMembership} from "../../../api/membershipTourApi";
import {useFullAccount} from "../../../hooks/AccountContext";

interface Props {
    shareEmergencyContacts: number[];
    setMessage: (msg: MessageType) => void;
    onSaved: () => void;
}

export default function TourCreateForm({
                                           shareEmergencyContacts,
                                           setMessage,
                                           onSaved
                                       }: Props) {
    const {t} = useTranslation();

    const {
        account,
        contactData: accountContactData,
        medData: accountMedData,
    } = useFullAccount();

    const [values, setValues] = useState<TourFormValues>({
        destination: "",
        dateFrom: "",
        timeFrom: "",
        dateTo: "",
        timeTo: ""
    });

    const handleChange = (field: keyof TourFormValues, value: string) => {
        setValues(prev => {
            // Default-Zeit setzen, wenn Datum gesetzt wird
            if (field === "dateFrom" && prev.timeFrom === "") {
                return {...prev, dateFrom: value, timeFrom: "00:00"};
            }
            if (field === "dateTo" && prev.timeTo === "") {
                return {...prev, dateTo: value, timeTo: "00:00"};
            }

            return {...prev, [field]: value};
        });
    };

    const isValidRange =
        values.dateFrom &&
        values.timeFrom &&
        values.dateTo &&
        values.timeTo &&
        new Date(`${values.dateTo}T${values.timeTo}`) >=
        new Date(`${values.dateFrom}T${values.timeFrom}`);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);

        if (!isValidRange) {
            setMessage({type: "error", text: t("tours.messages.invalidDateRange")});
            return;
        }

        try {
            const tourDto = {
                destination: values.destination,
                validFrom: localInputToUtc(`${values.dateFrom}T${values.timeFrom}`),
                validTo: localInputToUtc(`${values.dateTo}T${values.timeTo}`)
            };

            var tour = await createTour(tourDto);

            const membershipDto = {
                role: UserRole.CREATOR,
                shareContactData: accountContactData.shareData,
                shareMedicalData: accountMedData.shareData,
                shareEmergencyContacts: shareEmergencyContacts
            }

            await createTourMembership(account.id, tour.id, membershipDto);

            setMessage({type: "success", text: t("common.messages.success")});
            onSaved();
        } catch {
            setMessage({type: "error", text: t("common.messages.error")});
        }
    };

    return (
        <form onSubmit={handleSubmit} className="form-container">
            <TourEditForm values={values} disableFields={false} onChange={handleChange}/>

            <button type="submit" className="form-button" disabled={!isValidRange}
                    title={isValidRange ? t("tours.messages.invalidDateRange") : undefined}>
                {t("common.button.save")}
            </button>
        </form>
    );
}
